import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type { OrgUnitOption } from '../organization/organizationTypes';
import type { RecruitmentUserOption } from '../vacancies/vacancyTypes';
import type {
  AttritionCategoryDetail,
  AttritionCategoryListItem,
  AttritionCategoryRequest,
} from './attritionCategoryTypes';

function toQueryString(query: ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Present-but-empty is "ticked to nothing", which matches no row — so `!== undefined`.
  if (query.status !== undefined) params.set('status', query.status);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const BASE = '/api/attrition-categories';

export const attritionCategoriesApi = {
  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<AttritionCategoryListItem>>(`${BASE}${toQueryString(query)}`, { signal }),

  /** The live categories, each with its live checklist — the request form's picker. */
  options: (signal?: AbortSignal) => api<AttritionCategoryDetail[]>(`${BASE}/options`, { signal }),

  /** The live accounts a checklist line may name as its reviewer — this module's own door. */
  userOptions: (signal?: AbortSignal) => api<RecruitmentUserOption[]>(`${BASE}/user-options`, { signal }),

  /** The live org units a clearance line may name — this module's own door, no Organization key. */
  unitOptions: (signal?: AbortSignal) => api<OrgUnitOption[]>(`${BASE}/unit-options`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<AttritionCategoryDetail>(`${BASE}/${id}`, { signal }),

  create: (body: AttritionCategoryRequest) => api<AttritionCategoryDetail>(BASE, { method: 'POST', body }),

  /** The Details section's own write — name and description, nothing of the checklist. */
  update: (id: string, body: { name: string; description: string | null }) =>
    api<AttritionCategoryDetail>(`${BASE}/${id}`, { method: 'PUT', body }),

  /** The Checklist section's own write — the lines, nothing of the details. */
  updateChecklist: (id: string, body: { documents: AttritionCategoryRequest['documents'] }) =>
    api<AttritionCategoryDetail>(`${BASE}/${id}/documents`, { method: 'PUT', body }),

  /** The Clearances section's own write — the units and their signatories, nothing else. */
  updateClearances: (id: string, body: { clearances: AttritionCategoryRequest['clearances'] }) =>
    api<AttritionCategoryDetail>(`${BASE}/${id}/clearances`, { method: 'PUT', body }),

  deactivate: (id: string) => api<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`${BASE}/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>(`${BASE}/clear-data`, { method: 'POST' }),
};
